import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	cloneElement,
	type HTMLAttributes,
	type ReactElement,
	type RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import { ManagerMultiSelect } from '@/features/marquee/components/manager-multi-select'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { ctasQueryOptions } from '@/features/marquee/query-options'
import {
	ctaValidationMessage,
	invalidStateMessage
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { changeBlanketCta, type MarqueeEventState } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface ChangeBlanketCtaDialogProps {
	eventId?: string
	/**
	 * The event's lifecycle state, used only to word a wrong-state refusal: the
	 * write answers the shared 409 `invalid-state`, which names no action.
	 */
	eventState?: MarqueeEventState
	/** Current CTA codes (first = lead), prefilled into the picker. */
	ctas?: string[]
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
}

/**
 * "Change blanket CTA" — swaps the event's pitched CTAs mid-event via
 * POST /cta-change (first selected code is the lead). Desktop → `Dialog`,
 * mobile → `Drawer`.
 *
 * **Presentation-only**, and the caller enforces that by not rendering it on a
 * tune-up (OPSSTD-4916: a tune-up has no blanket set, so there is nothing to
 * change — the action is absent rather than disabled). Two consequences follow
 * from that alone: the selection can never be emptied here, since a presentation
 * is what every later check-in snapshots, and the endpoint answers 422 for an
 * empty array in this mode.
 *
 * Why it exists at all, given the event form edits the same field: the form is
 * `pre_work`-only, so from the lock onward this dialog is the only way to correct
 * the selection. On a presentation the blanket set is the entire capture
 * mechanism, so without it a wrong selection found on the day is uncorrectable
 * for the whole event.
 *
 * Already checked-in attendees keep their snapshotted CTA — guaranteed by the
 * backend, and stated in the dialog so the operator does not expect otherwise.
 */
function ChangeBlanketCtaDialog({
	eventId,
	eventState,
	ctas: currentCtas,
	trigger
}: ChangeBlanketCtaDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const [open, setOpen] = useState(false)
	const [ctaValues, setCtaValues] = useState<string[]>(currentCtas ?? [])
	const [ctaError, setCtaError] = useState<string | null>(null)
	// Portal targets for the option list, one per surface (only one is mounted).
	const dialogContentRef = useRef<HTMLDivElement>(null)
	const drawerContentRef = useRef<HTMLDivElement>(null)

	const { data: ctas = [], isLoading: isLoadingCtas } = useQuery(
		ctasQueryOptions()
	)
	const ctaOptions = useMemo<ComboboxOption[]>(
		() => ctas.map((c) => ({ value: c.code, label: c.label })),
		[ctas]
	)

	// Re-seed the picker whenever the dialog opens against the current event.
	useEffect(() => {
		if (open) {
			setCtaValues(currentCtas ?? [])
			setCtaError(null)
		}
	}, [open, currentCtas])

	const queryClient = useQueryClient()

	// Already checked-in rows keep their snapshotted CTA, so semantically only
	// the event detail changes; the invalidation targets the shared event prefix
	// because invalidating a parent and one of its children separately is not
	// allowed.
	const changeCta = useMutation({
		mutationFn: () =>
			changeBlanketCta({
				path: { eventId: eventId ?? '' },
				body: { ctas: ctaValues }
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			toast.success(t('marquee:checkin_cta_saved'))
			setOpen(false)
		},
		onError: (error) => {
			// The server enforces the per-mode rule too (422 `validation-error`
			// with the detail under `errors.ctas`), so its message lands on the
			// field rather than in a toast.
			const message = ctaValidationMessage(error)
			if (message) {
				setCtaError(message)
				return
			}
			// 409 `invalid-state` means the event left `live` under us (a second
			// tab closed it). The code is the one seven writes share, so the
			// sentence comes from the shared mapping rather than from here.
			const refusal = invalidStateMessage({
				error,
				action: 'change_blanket_cta',
				eventState
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	// A presentation must always keep at least one code. The rule is stated under
	// the field while the selection is empty and re-stated as an error here, so
	// the refusal is always explained: a disabled button that never says why is
	// the client-side surprise this replaces. The endpoint's own 422 stays the
	// backstop, surfaced by `ctaValidationMessage` in `onError`.
	const handleSave = () => {
		if (ctaValues.length === 0) {
			setCtaError(t('marquee:error_ctas_required'))
			return
		}
		setCtaError(null)
		changeCta.mutate()
	}
	// The option list portals into the surrounding modal rather than the body:
	// both the drawer and the dialog disable pointer events outside their own
	// subtree, so a list left on the body cannot be scrolled or its search
	// focused, and tapping it reads as an interaction with the background.
	const body = (container: RefObject<HTMLElement | null>) => (
		<div className='flex flex-col gap-1.5'>
			<Label>{t('marquee:form_ctas_label')}</Label>
			<ManagerMultiSelect
				emptyLabel={t('marquee:combobox_empty')}
				hasError={!!ctaError}
				loading={isLoadingCtas}
				onChange={(value) => {
					setCtaError(null)
					setCtaValues(value)
				}}
				options={ctaOptions}
				placeholder={t('marquee:cta_placeholder')}
				popupContainer={container}
				removeLabel={(name) => t('marquee:ctas_remove', { name })}
				searchPlaceholder={t('marquee:combobox_search_placeholder')}
				value={ctaValues}
			/>
			{/* The rule is stated while the selection is empty, rather than left
			    for the operator to infer from a button that will not respond. */}
			<InputHelpText
				error={ctaError ?? undefined}
				helpText={
					ctaValues.length === 0
						? t('marquee:checkin_cta_dialog_empty_hint')
						: undefined
				}
			/>
		</div>
	)

	const actions = (
		<>
			<Button
				onClick={() => setOpen(false)}
				type='button'
				variant='tertiary'
			>
				{t('marquee:cancel')}
			</Button>
			<Button
				disabled={changeCta.isPending}
				onClick={handleSave}
				type='button'
				variant='primary'
			>
				{t('marquee:checkin_cta_dialog_confirm')}
				{changeCta.isPending && <Spinner size={16} />}
			</Button>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog onOpenChange={setOpen} open={open}>
				<DialogTrigger render={trigger} />
				<DialogContent
					className='flex max-w-120 flex-col'
					ref={dialogContentRef}
				>
					<DialogBody className='gap-4 overflow-visible pb-0'>
						<div className='flex flex-col gap-1'>
							<DialogTitle>
								{t('marquee:checkin_cta_dialog_title')}
							</DialogTitle>
							<DialogDescription className='text-neutral-500'>
								{t('marquee:checkin_cta_dialog_description')}
							</DialogDescription>
						</div>
					</DialogBody>
					<div className='min-h-0 flex-1 overflow-y-auto px-6 pb-6'>
						{body(dialogContentRef)}
					</div>
					<DialogFooter className='flex flex-row justify-between'>
						{actions}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={setOpen} open={open}>
			<DrawerTrigger asChild>
				{cloneElement(trigger, {
					onPointerDown: (e: React.PointerEvent) => e.preventDefault()
				})}
			</DrawerTrigger>
			<DrawerContent ref={drawerContentRef}>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{t('marquee:checkin_cta_dialog_title')}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{t('marquee:checkin_cta_dialog_description')}
					</DrawerDescription>
				</DrawerHeader>
				<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-1'>
					{body(drawerContentRef)}
				</div>
				<DrawerFooter className='flex-row justify-between'>
					{actions}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export { ChangeBlanketCtaDialog }
